/* eslint-disable no-unused-expressions */
/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import {
  selectActiveListings,
  selectAllListings,
  selectInactiveListings,
  selectIsAddressInactive,
  selectIsOnlyActiveUnitOfMultiUnit,
  selectIsSingleUnit,
} from '@zcode-ui/perm-address'
import { isDetailPageNew, selectIsAddressOnBuilding } from '@zcode-ui/web-zumper-global-state'
import type { TopListable } from '@zcode/bizos'
import { ListingStatus, getEnumTitle } from '@zcode/enums'
import { PAGE_NAMES, PROP_NAMES } from '@zcode/mixpanel'
import { selectDisplayMedia } from '@zcode/nr/es/shared/bizos/rentable/selectors'
import { displayAmenity, displayBuildingAmenity } from '@zcode/nr/es/shared/utils/amenity'
import type {
  Listable,
  MaybeEmptyProperty,
  Pin,
  RawPinInventorySearch,
  State,
} from '@zcode/state-type'
import type { PageName } from './types'

const getDetailPageName = ({
  isAddress,
  isAddressInactive,
  isAddressOnBuilding,
  isActive,
  isBuilding,
  isMultiUnitAddressUnit,
  isNewDetail,
  isOnlyActiveUnitOfMultiUnit,
  isSingleUnitAddress,
}: {
  isAddress: boolean
  isAddressInactive: boolean
  isAddressOnBuilding: boolean
  isActive: boolean
  isBuilding: boolean
  isMultiUnitAddressUnit: boolean
  isNewDetail: boolean
  isOnlyActiveUnitOfMultiUnit: boolean
  isSingleUnitAddress: boolean
}) => {
  if (!isAddress && !isAddressOnBuilding) {
    return `${isActive ? 'Active' : 'Inactive'} ${isNewDetail ? 'New ' : ''}${isBuilding ? 'Building' : 'Listing'}` as const
  }
  const addressPageTypeEnd = isOnlyActiveUnitOfMultiUnit
    ? ' - Single Active Unit'
    : isMultiUnitAddressUnit
      ? ' - Unit'
      : ''
  return `${isAddressInactive ? 'Inactive' : 'Active'} New ${isSingleUnitAddress ? 'Single' : 'Multi'} Address${
    addressPageTypeEnd
  }` as const
}

export const getDetailProps = (
  entity: Listable | Pin<RawPinInventorySearch> | MaybeEmptyProperty | TopListable,
  state: State,
  pageName?: PageName | (string & {})
) => {
  const {
    isActive,
    isBuilding,
    bathrooms,
    bedrooms,
    catsOk,
    dateAvailable,
    description,
    dogsOk,
    price,
    squareFeet,
    status,
    unitAmenities,
  } = entity

  const isAddressInactive = selectIsAddressInactive(state)
  const isNewDetail = isDetailPageNew(state)
  const isAddress = !!selectAllListings(state)?.length
  const isAddressOnBuilding = selectIsAddressOnBuilding(state)
  const isSingleUnitAddress = selectIsSingleUnit(state)
  let isMultiUnitAddressUnit = false

  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search)
    if (searchParams.has('listing_id')) {
      isMultiUnitAddressUnit = true
    }
  }

  const activeListings: number[] = []
  const inactiveListings: number[] = []

  if ((isAddress || isAddressOnBuilding) && !isSingleUnitAddress) {
    const activeAddressListings = selectActiveListings(state)
    const inactiveAddressListings = selectInactiveListings(state)

    activeAddressListings.map(({ listingId }) => {
      activeListings.push(listingId)
    })

    inactiveAddressListings.map(({ listingId }) => {
      inactiveListings.push(listingId)
    })
  } else if (isBuilding) {
    for (const { isActive, listingId } of entity.floorplans ?? []) {
      if (isActive) {
        activeListings.push(listingId)
      } else {
        inactiveListings.push(listingId)
      }
    }
  }

  const isOnlyActiveUnitOfMultiUnit = selectIsOnlyActiveUnitOfMultiUnit(state)
  const detailPageName = getDetailPageName({
    isActive: !!isActive,
    isAddress,
    isAddressOnBuilding,
    isAddressInactive,
    isMultiUnitAddressUnit,
    isNewDetail,
    isSingleUnitAddress,
    isOnlyActiveUnitOfMultiUnit,
    isBuilding: !!entity.isBuilding,
  })

  if (pageName === PAGE_NAMES.PERM_ADDRESS) {
    return {
      [PROP_NAMES.DETAIL_PAGE_TYPE]: detailPageName,
      ...(activeListings.length > 0 && { [PROP_NAMES.ACTIVE_LISTINGS]: activeListings }),
      ...(inactiveListings.length > 0 && { [PROP_NAMES.INACTIVE_LISTINGS]: inactiveListings }),
    }
  }

  const buildingAmenityLabels: string[] = []
  const listingAmenityLabels: string[] = []
  const buildingAmenities =
    entity.isBuilding && entity.listingId == null ? entity.amenities : entity.buildingAmenities
  if (buildingAmenities) {
    for (const amenity of buildingAmenities) {
      buildingAmenityLabels.push(displayBuildingAmenity(amenity))
    }
  }
  const listingAmenities = entity.isBuilding ? unitAmenities : entity.amenities
  if (listingAmenities) {
    for (const amenity of listingAmenities) {
      listingAmenityLabels.push(displayAmenity(amenity))
    }
  }

  const media = selectDisplayMedia(entity)

  return {
    [PROP_NAMES.BUILDING_AMENITIES]: buildingAmenityLabels,
    [PROP_NAMES.BUILDING_PETS]: entity.isBuilding ? !!(catsOk || dogsOk) : undefined,
    [PROP_NAMES.DESCRIPTION_LENGTH]: description?.length,
    [PROP_NAMES.LISTING_AMENITIES]: listingAmenityLabels,
    [PROP_NAMES.LISTING_AVAIL_DATE]: dateAvailable,
    [PROP_NAMES.LISTING_BATHS]: bathrooms,
    [PROP_NAMES.LISTING_BEDS]: bedrooms,
    [PROP_NAMES.LISTING_PETS]: !entity.isBuilding ? !!(catsOk || dogsOk) : undefined,
    [PROP_NAMES.LISTING_PRICE]: price,
    [PROP_NAMES.NUM_IMAGES]: media?.length,
    [PROP_NAMES.SQUARE_FOOTAGE]: squareFeet,
    [PROP_NAMES.PROPERTY_STATUS]:
      status === undefined ? undefined : getEnumTitle(ListingStatus, status),
    [PROP_NAMES.DETAIL_PAGE_TYPE]: detailPageName,
    ...(activeListings.length > 0 && { [PROP_NAMES.ACTIVE_LISTINGS]: activeListings }),
    ...(inactiveListings.length > 0 && { [PROP_NAMES.INACTIVE_LISTINGS]: inactiveListings }),
  }
}
